import { Component, Prop, h } from '@stencil/core';

import bannerActions from '../../../actions/actions.interactive-banner';
import bannerValidateActions from '../../../actions/actions.validation.interactive-banner';
import { SelectOption } from '../../../interfaces/interface.banner';
import { state as bannerState } from '../../../store/store.interactive-banner';
import { shiftDigitalTradeInVehicleIndicated } from '../../../tracking/tracking.interactiveBanner';
import { parseMileage } from '../../../utils/utils.interactive-banner';

// MSM = Model Style Mileage
@Component({
  tag: 'banner-msm',
  styleUrl: 'banner-msm.css',
  shadow: true,
})
export class BannerMsm {
  @Prop() model: string;
  @Prop() trim: string;
  @Prop() vin: string;
  @Prop() trimId: number;
  @Prop() vehiclePossibilities: SelectOption[];
  @Prop() yearId: number;
  @Prop() make: string;
  @Prop() hasMultipleTrims: boolean;
  @Prop() showDesktop: boolean = false;
  @Prop({ mutable: true }) hasStyleError: boolean = false;
  @Prop({ mutable: true }) hasMileageError: boolean = false;
  @Prop() errorMessage: string;
  @Prop() isActiveListing: boolean = false;
  @Prop() activeDealerListingUrl: string;

  // Called once just after the component is first connected to the DOM.
  componentWillLoad() {
    if (bannerState.mileage) {
      this.validateMileage();
    }
  }

  onStyleChanged(evt) {
    this.trimId = parseInt(evt.detail);
    this.validateStyle();
  }

  validateStyle() {
    this.hasStyleError = !bannerValidateActions.onValidateMSMStyle(this.hasMultipleTrims, !!this.trimId);
  }

  validateMileage() {
    this.hasMileageError = !bannerValidateActions.onValidateMSMMileage(bannerState.mileage);
  }

  validateMileageRange() {
    this.hasMileageError = !bannerValidateActions.onValidateMileageRange(bannerState.mileage);
  }

  onMileageChanged(evt) {
    bannerActions.updateMileageStatus(parseInt(evt.detail));
    this.validateMileage();
  }

  hasError() {
    return this.hasStyleError || this.hasMileageError;
  }

  async onNextClicked() {
    shiftDigitalTradeInVehicleIndicated();
    this.validateMileage();
    this.validateStyle();
    if (this.hasError()) {
      return;
    }
    await bannerActions.onFetchVehicleTypicalMileage(this.trimId);
    this.validateMileageRange();
    if (this.hasError()) {
      return;
    }
    await bannerActions.onMileageSubmit(this.trimId, bannerState.mileage);
  }
  onInputBlur() {
    this.validateMileage();
    this.validateStyle();
    if (this.hasError()) {
      return;
    }
  }

  render() {
    const mileageColumn = {
      flex: true,
      'w-full': !this.hasMultipleTrims,
      'space-x-3': !this.showDesktop,
      'space-x-4': this.showDesktop,
    };
    const vinClass = {
      'text-xs/8': !this.showDesktop,
      'text-xs/4': this.showDesktop,
      hidden: !this.vin,
    };
    const bannerMsmClass = {
      'my-auto': true,
      'flex flex-col': this.showDesktop,
    };
    const bannerMsmInputGroupClass = {
      flex: true,
      'flex-col': !this.showDesktop,
      'flex-row': this.showDesktop,
    };
    const styleSelectorClass = {
      'w-full': true,
      'mb-3': !this.showDesktop,
      'mb-0 mr-3': this.showDesktop,
    };
    const msmNextButtonClass = {
      'w-20': this.showDesktop,
    };
    const errorMessageDesktopClass = {
      'h-0': this.showDesktop,
    };

    const titleWidth = {
      'banner-msm-title-width': true,
      'max-w-[90vw]': !this.showDesktop,
      'max-w-[500px]': this.showDesktop,
    };
    const tooltipWidth = {
      'banner-msm-tooltip-width': true,
      'max-w-[85vw]': !this.showDesktop,
      'max-w-[30vw]': this.showDesktop,
    };
    const bannerMsmDisplayVehicleTitleClass = {
      flex: true,
      'justify-center': !this.showDesktop,
    };
    const titleYourVehicleClass = {
      hidden: this.showDesktop,
      'text-xs/3': true,
    };

    const carName = `${this.yearId} ${this.make} ${this.model} ${this.hasMultipleTrims ? '' : this.trim}`;
    return (
      <div class={bannerMsmClass}>
        <div class="text-left">
          <div class={bannerMsmDisplayVehicleTitleClass}>
            <div class="tooltip w-full">
              <span class={titleYourVehicleClass}>Your vehicle:</span>
              <div class={titleWidth}>{carName}</div>
              <div class={tooltipWidth}>
                <div>{carName}</div>
              </div>
            </div>
          </div>
          <div class={vinClass}>VIN: {this.vin}</div>
        </div>
        {!this.isActiveListing && (
          <div class="flex flex-col mt-2 pb-2">
            <div class={bannerMsmInputGroupClass}>
              {this.hasMultipleTrims && (
                <input-select-secondary
                  class={styleSelectorClass}
                  id="style"
                  label="Style"
                  options={this.vehiclePossibilities}
                  onValueChange={(e) => this.onStyleChanged(e)}
                  hasError={this.hasStyleError}
                />
              )}
              <div class={mileageColumn}>
                <input-text-box
                  class="w-full"
                  id="mileage"
                  onValueChange={this.onMileageChanged.bind(this)}
                  onBlur={this.onInputBlur.bind(this)}
                  label="Mileage"
                  maxLength={6}
                  hasGrayPlaceHolder
                  value={bannerState.mileage ? bannerState.mileage.toString() : ''}
                  parse={parseMileage}
                />
                <ico-button label="Next" buttonSize="large" class={msmNextButtonClass} onClick={this.onNextClicked.bind(this)} />
              </div>
            </div>
            <banner-error hasError={this.hasError()} errorMessage={this.errorMessage} class={errorMessageDesktopClass}></banner-error>
          </div>
        )}
        {this.isActiveListing && <banner-active-listing activeDealerListingUrl={this.activeDealerListingUrl} />}
      </div>
    );
  }
}
